import dayjs, { Dayjs } from 'dayjs'
import { Timestamp } from 'firebase/firestore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
// Add plugins to dayjs
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(utc)
dayjs.extend(timezone)

export const APP_TIMEZONE = 'Europe/Athens'

type TimestampLike =
  | Timestamp
  | {
      seconds?: number
      nanoseconds?: number
      _seconds?: number
      _nanoseconds?: number
      toDate?: () => Date
    }

const isDayjsValue = (value: Date | TimestampLike | Dayjs | string): value is Dayjs => dayjs.isDayjs(value)

const isDateValue = (value: Date | TimestampLike | Dayjs | string): value is Date => value instanceof Date

const hasToDate = (
  value: Date | TimestampLike | Dayjs | string
): value is {
  toDate: () => Date
} => {
  return typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function'
}

const hasSerializedTimestampFields = (
  value: Date | TimestampLike | Dayjs | string
): value is {
  seconds?: number
  nanoseconds?: number
  _seconds?: number
  _nanoseconds?: number
} => {
  return typeof value === 'object' && value !== null
}

export const nowInAppTimezone = (): Dayjs => dayjs().tz(APP_TIMEZONE)

export const startOfTodayInAppTimezone = (): Dayjs => nowInAppTimezone().startOf('day')

export const startOfTomorrowInAppTimezone = (): Dayjs => startOfTodayInAppTimezone().add(1, 'day')

export const startOfDayAfterTomorrowInAppTimezone = (): Dayjs => startOfTodayInAppTimezone().add(2, 'day')

export const startOfYesterdayInAppTimezone = (): Dayjs => startOfTodayInAppTimezone().subtract(1, 'day')

export const toAppDay = (value: Date | TimestampLike | Dayjs | string): Dayjs => {
  if (isDayjsValue(value)) {
    return value.tz(APP_TIMEZONE).startOf('day')
  }

  if (isDateValue(value)) {
    return dayjs(value).tz(APP_TIMEZONE).startOf('day')
  }

  if (typeof value === 'string') {
    return dayjs(value).tz(APP_TIMEZONE).startOf('day')
  }

  if (hasToDate(value)) {
    return dayjs(value.toDate()).tz(APP_TIMEZONE).startOf('day')
  }

  if (hasSerializedTimestampFields(value)) {
    const seconds = value.seconds ?? value._seconds
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0

    if (typeof seconds === 'number') {
      return dayjs(new Date(seconds * 1000 + nanoseconds / 1_000_000))
        .tz(APP_TIMEZONE)
        .startOf('day')
    }
  }

  if (typeof value === 'object') {
    return nowInAppTimezone().startOf('day')
  }

  return dayjs(value).tz(APP_TIMEZONE).startOf('day')
}

export const serializeBookingDateForApi = (value: Date | TimestampLike | Dayjs | string): string => toAppDay(value).toISOString()

export const formatTimestampInAppTimezone = (date: Timestamp, format = 'DD/MM/YYYY') => {
  if (!date || typeof date !== 'object') {
    return ''
  }

  return dayjs(date.toDate()).tz(APP_TIMEZONE).format(format)
}

export const dateToTimestamp = (date: dayjs.Dayjs) => {
  return Timestamp.fromDate(date.toDate())
}

export const dateToStartOfDayTimestamp = (date: dayjs.Dayjs) => {
  return Timestamp.fromDate(toAppDay(date).toDate())
}

export const dateToEndOfDayTimestamp = (date: dayjs.Dayjs) => {
  return Timestamp.fromDate(toAppDay(date).endOf('day').toDate())
}

export const timeToTimestamp = (time: dayjs.Dayjs) => {
  return Timestamp.fromDate(time.toDate())
}

export const timestampToDateString = (date: Timestamp) => {
  return formatTimestampInAppTimezone(date)
}

export const timestampToTimeString = (time: Timestamp) => {
  return formatTimestampInAppTimezone(time, 'HH:mm')
}

export const isPastDate = (date: Timestamp) => {
  if (!date) {
    return false
  }

  return toAppDay(date).isBefore(startOfTodayInAppTimezone())
}

export const timestampToDayjs = (date: Timestamp) => {
  return toAppDay(date)
}

export const shortStringDates = (dates: string[], order: 'asc' | 'desc' = 'asc') => {
  return dates.sort((a, b) => {
    const [dayA, monthA, yearA] = a.split('/').map(Number)
    const [dayB, monthB, yearB] = b.split('/').map(Number)
    const dateA = new Date(yearA, monthA - 1, dayA)
    const dateB = new Date(yearB, monthB - 1, dayB)
    return order === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime()
  })
}

export const shortStringDateToDate = (date: string): Date => {
  const [day, month, year] = date.split('/').map(Number)
  return dayjs
    .tz(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, APP_TIMEZONE)
    .startOf('day')
    .toDate()
}

export const formatShortStringDate = (date: Date): string => {
  return dayjs(date).tz(APP_TIMEZONE).format('DD/MM/YYYY')
}

export const isFutureDate = (date: string) => {
  const [day, month, year] = date.split('/').map(Number)
  const parsedDate = dayjs.tz(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, APP_TIMEZONE).startOf('day')
  return parsedDate.isAfter(startOfTodayInAppTimezone())
}

export const isSameDayjsTimestamp = (date1: dayjs.Dayjs, date2: Timestamp) => {
  const normalizedDate1 = toAppDay(date1)
  const normalizedDate2 = toAppDay(date2)
  return normalizedDate1.isSame(normalizedDate2)
}

export const isSameOrAfterDayjsTimestamp = (date1: dayjs.Dayjs, date2: Timestamp) => {
  const normalizedDate1 = toAppDay(date1)
  const normalizedDate2 = toAppDay(date2)
  return normalizedDate1.isSameOrAfter(normalizedDate2)
}

export const isSameOrBeforeDayjsTimestamp = (date1: dayjs.Dayjs, date2: Timestamp) => {
  const normalizedDate1 = toAppDay(date1)
  const normalizedDate2 = toAppDay(date2)
  return normalizedDate1.isSameOrBefore(normalizedDate2)
}

export const isSameOrAfterTimestamp = (timestamp1: Timestamp, timestamp2: Timestamp) => {
  const date1 = toAppDay(timestamp1)
  const date2 = toAppDay(timestamp2)
  return date1.isSameOrAfter(date2)
}

export const isSameOrBeforeTimestamp = (timestamp1: Timestamp, timestamp2: Timestamp) => {
  const date1 = toAppDay(timestamp1)
  const date2 = toAppDay(timestamp2)
  return date1.isSameOrBefore(date2)
}

export const areDateRangesOverlapping = (
  range1: { startDate: Date | TimestampLike | Dayjs | string; endDate: Date | TimestampLike | Dayjs | string },
  range2: { startDate: Date | TimestampLike | Dayjs | string; endDate: Date | TimestampLike | Dayjs | string }
) => {
  const range1Start = toAppDay(range1.startDate)
  const range1End = toAppDay(range1.endDate)
  const range2Start = toAppDay(range2.startDate)
  const range2End = toAppDay(range2.endDate)

  return range1Start.isSameOrBefore(range2End) && range1End.isSameOrAfter(range2Start)
}

export const getDateRangeDayKey = (startDate: Date | TimestampLike | Dayjs | string, endDate: Date | TimestampLike | Dayjs | string) => {
  return `${toAppDay(startDate).format('YYYY-MM-DD')}-${toAppDay(endDate).format('YYYY-MM-DD')}`
}

/**
 * A booking is eligible for a refund when the current date is on or before
 * the cutoff date: `bookingDate - refundCutoffDays`.
 *
 * cutoffDays = 0 means "same-day cancellation still gets a refund".
 */
export const isEligibleForRefund = (bookingDate: Timestamp, refundCutoffDays: number, noRefund = false): boolean => {
  if (noRefund) {
    return false
  }

  const cutoffDate = toAppDay(bookingDate).subtract(refundCutoffDays, 'day')
  return !startOfTodayInAppTimezone().isAfter(cutoffDate)
}
